import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { BadgeList } from '../components/Badges';
import { Button } from '../components/ui/button';
import { MapPin, Clock, Video, Building, Leaf, Wind, Brain, Hand, Sparkles, Calendar, ArrowLeft, Award, Languages, GraduationCap, Target, Mic, Heart, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTY_ICONS = {
  therapeute: Heart,
  naturopathe: Leaf,
  sophrologue: Wind,
  psychologue: Brain,
  osteopathe: Hand,
  hypnotherapeute: Sparkles,
  coach: Target,
  orthophoniste: Mic,
};

const SPECIALTY_LABELS = {
  therapeute: 'Thérapeute',
  naturopathe: 'Naturopathe',
  sophrologue: 'Sophrologue',
  psychologue: 'Psychologue',
  osteopathe: 'Ostéopathe',
  hypnotherapeute: 'Hypnothérapeute',
  coach: 'Coach',
  orthophoniste: 'Orthophoniste',
};

const LANGUAGE_NAMES = {
  fr: 'Français',
  en: 'Anglais',
  it: 'Italien',
  es: 'Espagnol',
  de: 'Allemand',
  ru: 'Russe',
  ro: 'Roumain',
  ar: 'Arabe',
  pt: 'Portugais',
  zh: 'Chinois'
};

const DAY_NAMES = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const PractitionerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [practitioner, setPractitioner] = useState(null);
  const [slots, setSlots] = useState([]);
  const [badges, setBadges] = useState([]);
  const [reviews, setReviews] = useState({ average_rating: 0, total_reviews: 0, reviews: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [practResponse, slotsResponse] = await Promise.all([
          axios.get(`${API}/practitioners/${id}`),
          axios.get(`${API}/slots/practitioner/${id}`)
        ]);
        setPractitioner(practResponse.data);
        setSlots(slotsResponse.data);
        
        // Fetch badges
        try {
          const badgesResponse = await axios.get(`${API}/practitioners/${id}/badges`);
          setBadges(badgesResponse.data.badges || []);
        } catch (e) {
          console.log('Badges not available');
        }
        
        // Fetch reviews
        try {
          const reviewsResponse = await axios.get(`${API}/practitioners/${id}/reviews`);
          setReviews(reviewsResponse.data);
        } catch (e) {
          console.log('Reviews not available');
        }
      } catch (error) {
        console.error('Error fetching practitioner:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      </div>
    );
  }

  if (!practitioner) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container-app py-20 text-center">
          <h1 className="font-heading text-2xl font-semibold mb-4">Praticien non trouvé</h1>
          <Link to="/search">
            <Button variant="outline">Retourner à la recherche</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = SPECIALTY_ICONS[practitioner.specialty] || Leaf;

  // Group slots by day
  const slotsByDay = slots.reduce((acc, slot) => {
    if (!acc[slot.day_of_week]) acc[slot.day_of_week] = [];
    acc[slot.day_of_week].push(slot);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="practitioner-profile-page">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft overflow-hidden" data-testid="practitioner-header">
              <div className="aspect-[3/1] relative">
                <img 
                  src={practitioner.photo_url 
                    ? (practitioner.photo_url.startsWith('/api/photos') || practitioner.photo_url.startsWith('/uploads')
                        ? `${process.env.REACT_APP_BACKEND_URL}${practitioner.photo_url}`
                        : practitioner.photo_url)
                    : `/images/default-practitioner.jpg`
                  }
                  alt={`${practitioner.user?.first_name} ${practitioner.user?.last_name}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              
              <div className="p-6 md:p-8 -mt-16 relative">
                <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6">
                  <div className="w-24 h-24 rounded-2xl border-4 border-white bg-sage-100 flex items-center justify-center shadow-soft overflow-hidden">
                    {practitioner.photo_url ? (
                      <img 
                        src={practitioner.photo_url.startsWith('/api/photos') || practitioner.photo_url.startsWith('/uploads')
                          ? `${process.env.REACT_APP_BACKEND_URL}${practitioner.photo_url}`
                          : practitioner.photo_url
                        }
                        alt={`${practitioner.user?.first_name}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <IconComponent className="w-10 h-10 text-sage-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="badge-specialty mb-2">
                      {SPECIALTY_LABELS[practitioner.specialty] || practitioner.specialty}
                    </span>
                    <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
                      {practitioner.user?.first_name} {practitioner.user?.last_name}
                    </h1>
                    {/* Badges */}
                    {badges.length > 0 && (
                      <div className="mt-2">
                        <BadgeList badges={badges} size="md" />
                      </div>
                    )}
                    {/* Rating Stars */}
                    {reviews.total_reviews > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${star <= Math.round(reviews.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {reviews.average_rating.toFixed(1)} ({reviews.total_reviews} avis)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {practitioner.city && (
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <MapPin className="w-5 h-5 text-sage-500" />
                      <span>
                        {practitioner.address && `${practitioner.address}, `}
                        {practitioner.city}
                        {practitioner.postal_code && ` ${practitioner.postal_code}`}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Clock className="w-5 h-5 text-sage-500" />
                    <span>{practitioner.consultation_duration} min / séance</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bio */}
            {practitioner.bio && (
              <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="practitioner-bio">
                <h2 className="font-heading text-xl font-medium mb-4">À propos</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {practitioner.bio}
                </p>
              </div>
            )}

            {/* Detailed Description */}
            {practitioner.detailed_description && (
              <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="practitioner-detailed">
                <h2 className="font-heading text-xl font-medium mb-4">Mon parcours et ma philosophie</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {practitioner.detailed_description}
                </p>
              </div>
            )}

            {/* Experience & Qualifications */}
            {(practitioner.years_experience || practitioner.diplomas?.length > 0 || practitioner.languages?.length > 0) && (
              <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="practitioner-qualifications">
                <h2 className="font-heading text-xl font-medium mb-4">Qualifications</h2>
                <div className="space-y-4">
                  {/* Experience */}
                  {practitioner.years_experience && (
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-terracotta-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Expérience</p>
                        <p className="text-muted-foreground">
                          {practitioner.years_experience} {practitioner.years_experience > 1 ? 'années' : 'année'} d'expérience
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Diplomas */}
                  {practitioner.diplomas?.length > 0 && (
                    <div className="flex items-start gap-3">
                      <GraduationCap className="w-5 h-5 text-sage-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Diplômes & Certifications</p>
                        <ul className="text-muted-foreground mt-1 space-y-1">
                          {practitioner.diplomas.map((diploma, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-sage-400" />
                              {diploma}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {/* Languages */}
                  {practitioner.languages?.length > 0 && (
                    <div className="flex items-start gap-3">
                      <Languages className="w-5 h-5 text-blue-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Langues parlées</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {practitioner.languages.map((lang) => (
                            <span 
                              key={lang} 
                              className="px-2 py-0.5 bg-sand-100 text-sand-700 rounded text-sm"
                            >
                              {LANGUAGE_NAMES[lang] || lang}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Consultation Types */}
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="consultation-types">
              <h2 className="font-heading text-xl font-medium mb-4">Types de consultation</h2>
              <div className="flex flex-wrap gap-4">
                {practitioner.consultation_types?.includes('cabinet') && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-sage-50 rounded-xl">
                    <Building className="w-5 h-5 text-sage-600" />
                    <div>
                      <p className="font-medium text-foreground">En cabinet</p>
                      <p className="text-sm text-muted-foreground">Consultation sur place</p>
                    </div>
                  </div>
                )}
                {practitioner.consultation_types?.includes('visio') && (
                  <div className="flex items-center gap-3 px-4 py-3 bg-terracotta-50 rounded-xl">
                    <Video className="w-5 h-5 text-terracotta-500" />
                    <div>
                      <p className="font-medium text-foreground">En visio</p>
                      <p className="text-sm text-muted-foreground">Consultation à distance</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Availability */}
            {Object.keys(slotsByDay).length > 0 && (
              <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="availability">
                <h2 className="font-heading text-xl font-medium mb-4">Disponibilités</h2>
                <div className="space-y-3">
                  {Object.entries(slotsByDay)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([day, daySlots]) => (
                      <div key={day} className="flex items-center gap-4">
                        <span className="w-24 font-medium text-foreground">
                          {DAY_NAMES[Number(day)]}
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {daySlots
                            .sort((a, b) => a.start_time.localeCompare(b.start_time))
                            .map((slot) => (
                              <span 
                                key={slot.id}
                                className="px-3 py-1 bg-sage-50 text-sage-700 rounded-lg text-sm"
                              >
                                {slot.start_time} - {slot.end_time}
                              </span>
                            ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            {reviews.reviews.length > 0 && (
              <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8" data-testid="reviews-section">
                <h2 className="font-heading text-xl font-medium mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Avis clients ({reviews.total_reviews})
                </h2>
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-sand-200">
                  <div className="text-3xl font-bold text-foreground">{reviews.average_rating.toFixed(1)}</div>
                  <div>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${star <= Math.round(reviews.average_rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground">{reviews.total_reviews} avis</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {reviews.reviews.map((review) => (
                    <div key={review.id} className="pb-4 border-b border-sand-100 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${star <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                          <span className="font-medium text-foreground">{review.client_name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-muted-foreground">{review.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 sticky top-24" data-testid="booking-card">
              <div className="text-center mb-6">
                {practitioner.price ? (
                  <>
                    <p className="text-3xl font-heading font-semibold text-foreground">
                      {practitioner.price}€
                    </p>
                    <p className="text-sm text-muted-foreground">
                      par consultation ({practitioner.consultation_duration} min)
                    </p>
                  </>
                ) : (
                  <p className="text-muted-foreground">Tarif sur demande</p>
                )}
              </div>

              <Link to={`/booking/${practitioner.id}`}>
                <Button 
                  className="w-full h-12 bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-xl font-medium"
                  data-testid="book-appointment-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Prendre rendez-vous
                </Button>
              </Link>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Paiement sur place
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PractitionerProfilePage;
